// Tiny markdown renderer for AI chat replies.
//
// Supports the subset Claude/OpenAI/Google models actually emit:
//   **bold**, *italic*, `inline code`
//   # heading, ## heading, ### heading
//   - bullet lists, * bullet lists, 1. numbered lists
//   GFM pipe tables (header row + |---| separator + body rows)
//   blank lines as paragraph breaks
//
// We deliberately do NOT pull in react-markdown / remark-gfm — those add
// ~55 KB minified. This handler covers everything the assistant currently
// produces and stays under 100 lines.

import type { ReactNode } from 'react';

// ---------- inline ----------

const INLINE_RE = /\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|_([^_]+)_|`([^`]+)`/g;

function renderInline(text: string, keyPrefix = ''): ReactNode[] {
  const out: ReactNode[] = [];
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  // Reset before each scan since the regex object is module-scoped.
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text))) {
    if (m.index > lastIdx) out.push(text.slice(lastIdx, m.index));
    const k = `${keyPrefix}-${m.index}`;
    if (m[1] || m[2])      out.push(<strong key={k}>{m[1] || m[2]}</strong>);
    else if (m[3] || m[4]) out.push(<em key={k}>{m[3] || m[4]}</em>);
    else if (m[5])         out.push(<code key={k} className="md-code">{m[5]}</code>);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) out.push(text.slice(lastIdx));
  return out;
}

// ---------- table detection ----------

// Separator row looks like `|---|---|` with optional alignment colons.
const SEPARATOR_RE = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/;

function splitRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((c) => c.trim());
}

// ---------- block parser ----------

export function renderMarkdown(src: string): ReactNode {
  if (!src) return null;
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const next = lines[i + 1];

    // Table: header row + pipe-separator row + body rows.
    if (line.includes('|') && next && SEPARATOR_RE.test(next)) {
      const headers = splitRow(line);
      const rows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && lines[j].includes('|') && lines[j].trim() !== '') {
        rows.push(splitRow(lines[j]));
        j++;
      }
      blocks.push(
        <table key={`tbl-${i}`} className="md-table">
          <thead>
            <tr>{headers.map((h, hi) => <th key={hi}>{renderInline(h, `h-${i}-${hi}`)}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>{row.map((c, ci) => <td key={ci}>{renderInline(c, `c-${i}-${ri}-${ci}`)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      );
      i = j;
      continue;
    }

    // Heading 1–3 → h3/h4/h5 (we keep heading hierarchy below the panel
    // header, which is already an h-level surface).
    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      const Tag = (`h${level + 2}`) as 'h3' | 'h4' | 'h5';
      blocks.push(<Tag key={`h-${i}`} className="md-h">{renderInline(heading[2], `hi-${i}`)}</Tag>);
      i++;
      continue;
    }

    // Lists (consecutive bullet or numbered lines collapse into one list).
    const isBullet = /^\s*[-*]\s+/.test(line);
    const isNumber = /^\s*\d+\.\s+/.test(line);
    if (isBullet || isNumber) {
      const items: ReactNode[] = [];
      let j = i;
      const re = isBullet ? /^\s*[-*]\s+(.*)$/ : /^\s*\d+\.\s+(.*)$/;
      while (j < lines.length && re.test(lines[j])) {
        const m = lines[j].match(re)!;
        items.push(<li key={j}>{renderInline(m[1], `li-${j}`)}</li>);
        j++;
      }
      const Tag = isBullet ? 'ul' : 'ol';
      blocks.push(<Tag key={`l-${i}`} className="md-list">{items}</Tag>);
      i = j;
      continue;
    }

    // Blank line — drop a small spacer so paragraphs breathe.
    if (line.trim() === '') {
      blocks.push(<div key={`s-${i}`} style={{ height: 6 }} />);
      i++;
      continue;
    }

    // Plain paragraph.
    blocks.push(<p key={`p-${i}`} className="md-p">{renderInline(line, `pi-${i}`)}</p>);
    i++;
  }

  return <div className="md">{blocks}</div>;
}
